import {
  type APIError,
  type NotificationCenterPreference,
  type NotificationPreferencePatch,
  type NotificationSettingsGetInput,
  type NotificationSettingsOutput,
  type NotificationSettingsUpdateInput,
  messageFromError,
  isDefined,
} from '@terros/common'
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { mutationUpdate, type GetHookResponse, type UpdateHookResponse } from '../hooks'
import { useServerRequest } from '../api'
import { optimisticallyMute, type OptimisticChange } from './optimistic'
import { isNotificationUnauthorized } from './count'

export function changedNotificationPreferences(input: {
  saved: NotificationCenterPreference[]
  draft: NotificationCenterPreference[]
}): NotificationPreferencePatch[] {
  return input.draft
    .filter((entry) => input.saved.find((saved) => saved.topic === entry.topic)?.push !== entry.push)
    .map(({ topic, push }) => ({ topic, channel: 'push', enabled: push }))
}

export function useNotificationSettings({
  enabled = true,
}: { enabled?: boolean } = {}): GetHookResponse<NotificationSettingsOutput> & { unavailable: boolean } {
  const request = useServerRequest<NotificationSettingsGetInput, NotificationSettingsOutput>(
    '/notification/settings/get'
  )
  const pending =
    useIsMutating({
      mutationKey: ['notification-center', 'settings-update'],
      predicate: (mutation) => {
        const input = mutation.state.variables
        return typeof input === 'object' && isDefined(input) && 'mute' in input
      },
    }) > 0
  const query = useQuery({
    queryKey: ['notification-center', 'settings'],
    queryFn: () => request({}),
    staleTime: 0,
    enabled: (query) => enabled && !pending && !isNotificationUnauthorized(query.state.error),
    retry: false,
  })
  return {
    ...query,
    lastUpdatedAt: query.dataUpdatedAt,
    error: messageFromError(query.error),
    unavailable: isNotificationUnauthorized(query.error),
  }
}

export function useUpdateNotificationSettings(): UpdateHookResponse<
  NotificationSettingsUpdateInput,
  NotificationSettingsOutput
> {
  const request = useServerRequest<NotificationSettingsUpdateInput, NotificationSettingsOutput>(
    '/notification/settings/update'
  )
  const client = useQueryClient()
  const mutation = useMutation<NotificationSettingsOutput, APIError, NotificationSettingsUpdateInput, OptimisticChange>(
    {
      mutationKey: ['notification-center', 'settings-update'],
      mutationFn: (input) => request(input),
      onMutate: (input) => optimisticallyMute(client, input),
      onError: (_error, _input, change) => change?.rollback(),
      // A failed response can follow a committed write. Reconcile after the last optimistic change.
      onSettled: (_data, _error, _input, change) => change?.finish(),
    }
  )
  return mutationUpdate(mutation, mutation.mutateAsync)
}
